import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from '../core/auth.service';
import { AdminApiService } from './admin/admin-api.service';
import { SystemApp } from './admin/models';
import { environment } from '../../environments/environment';
import { firstValueFrom } from 'rxjs';
import { filter, take } from 'rxjs/operators';

@Component({
  standalone: true,
  selector: 'app-apps',
  imports: [
    CommonModule,
    CardModule,
    ButtonModule,
    ProgressSpinnerModule,
    TranslateModule,
  ],
  styles: [`
    :host {
      display: block;
    }
    .header {
      display: grid;
      gap: 8px;
      margin-bottom: 24px;
    }
    .title {
      font-size: 28px;
      font-weight: 800;
      color: #111827;
      margin: 0;
    }
    .sub {
      color: #6b7280;
      line-height: 1.6;
      margin: 0;
    }
    .apps-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
      gap: 20px;
    }
    .app-card {
      background: #fff;
      border: 1px solid #e6e7eb;
      border-radius: 14px;
      padding: 24px;
      transition: all 0.2s ease;
      cursor: pointer;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }
    .app-card:hover {
      border-color: #3270d1;
      box-shadow: 0 4px 12px rgba(50, 112, 209, 0.15);
      transform: translateY(-2px);
    }
    .app-card.disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    .app-card.disabled:hover {
      transform: none;
      box-shadow: none;
    }
    .app-icon {
      width: 56px;
      height: 56px;
      border-radius: 12px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 24px;
      font-weight: 700;
      color: white;
      margin-bottom: 8px;
    }
    .app-icon.lab {
      background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
    }
    .app-icon.curalab {
      background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
    }
    .app-icon.hr {
      background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
    }
    .app-icon.insurance {
      background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
    }
    .app-icon.patient {
      background: linear-gradient(135deg, #0d9488 0%, #14b8a6 100%);
    }
    .app-icon.doctor {
      background: linear-gradient(135deg, #2563eb 0%, #60a5fa 100%);
    }
    .app-icon.referrallab {
      background: linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%);
    }
    .app-name {
      font-size: 20px;
      font-weight: 700;
      color: #111827;
      margin: 0;
    }
    .app-description {
      font-size: 14px;
      color: #6b7280;
      margin: 0;
    }
    .loading-container {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 400px;
    }
    .empty-state {
      text-align: center;
      padding: 60px 20px;
      color: #6b7280;
    }
    .empty-state-icon {
      font-size: 48px;
      margin-bottom: 16px;
      opacity: 0.5;
    }
    @media (max-width: 640px) {
      .apps-grid {
        grid-template-columns: 1fr;
      }
    }
  `],
  template: `
    <div class="header">
      <h1 class="title">{{ 'APPS.TITLE' | translate }}</h1>
      <p class="sub">{{ 'APPS.SUBTITLE' | translate }}</p>
    </div>

    <div class="loading-container" *ngIf="loading">
      <p-progressSpinner></p-progressSpinner>
    </div>

    <div class="empty-state" *ngIf="!loading && availableApps.length === 0">
      <div class="empty-state-icon">📱</div>
      <h3 style="margin: 0 0 8px 0; color: #111827;">{{ 'APPS.NO_APPS' | translate }}</h3>
      <p style="margin: 0;">{{ 'APPS.NO_APPS_DESC' | translate }}</p>
    </div>

    <div class="apps-grid" *ngIf="!loading && availableApps.length > 0">
      <div 
        class="app-card" 
        [class.disabled]="!hasAccessToApp(app)"
        (click)="openApp(app)"
        *ngFor="let app of availableApps"
      >
        <div class="app-icon" [class]="app.key.toLowerCase()">
          {{ getAppIcon(app.key) }}
        </div>
        <h3 class="app-name">{{ app.displayName }}</h3>
        <p class="app-description">{{ getAppDescription(app.key) }}</p>
      </div>
    </div>
  `,
})
export class AppsComponent implements OnInit {
  auth = inject(AuthService);
  router = inject(Router);
  api = inject(AdminApiService);
  http = inject(HttpClient);
  private translate = inject(TranslateService);

  get accessToken(): string | null {
    return this.auth.accessToken;
  }

  allSystems: SystemApp[] = [];
  availableApps: SystemApp[] = [];
  allowedSystemKeys = new Set<string>();
  loading = true;

  ngOnInit() {
    void this.loadApps();
  }

  async loadApps() {
    this.loading = true;
    try {
      await firstValueFrom(this.auth.ready$.pipe(filter((r) => r), take(1)));
      const systems = await firstValueFrom(this.api.listMySystems());
      this.allSystems = systems.filter((s) => s.isActive !== false);
      await this.mergeAllowedSystemKeysFromProfile();
      this.filterAvailableApps();
    } catch (err) {
      console.error('Error loading systems:', err);
      this.allSystems = [];
      this.availableApps = [];
    } finally {
      this.loading = false;
    }
  }

  /** يملأ allowedSystemKeys من قاعدة البيانات عبر /api/account/me — منفصل عن listSystems حتى لا يُفرّغ الشبكة إذا فشل أحد الطلبين. */
  private async mergeAllowedSystemKeysFromProfile() {
    this.allowedSystemKeys.clear();
    const apply = (me: { allowedSystemKeys?: string[] } | null | undefined) => {
      for (const k of me?.allowedSystemKeys ?? []) {
        const normalized = this.normalizeSystemKey(k);
        if (normalized) this.allowedSystemKeys.add(normalized);
      }
    };

    try {
      const me = await firstValueFrom(
        this.http.get<{ allowedSystemKeys?: string[] }>(`${environment.apiBase}/api/account/me`)
      );
      apply(me);
    } catch (e) {
      console.warn('Could not load /api/account/me; will rely on token permissions if possible.', e);
      try {
        await this.auth.refreshToken();
        const me2 = await firstValueFrom(
          this.http.get<{ allowedSystemKeys?: string[] }>(`${environment.apiBase}/api/account/me`)
        );
        apply(me2);
      } catch {
        // يبقى الاعتماد على مطالبات perm في التوكن في hasAccessToApp
      }
    }
  }

  filterAvailableApps() {
    // Filter apps based on user permissions
    this.availableApps = this.allSystems.filter(app => {
      return this.hasAccessToApp(app);
    });
  }

  hasAccessToApp(app: SystemApp): boolean {
    const systemKey = this.normalizeSystemKey(app.key);
    // المستخدم داخل البوابة بالفعل — بطاقة «البوابة» للتنقل/المعلومات دون اشتراط صلاحية auth:*
    if (systemKey === 'auth') {
      return true;
    }
    if (this.allowedSystemKeys.has(systemKey)) {
      return true;
    }
    return this.auth.hasPermission(`${systemKey}:*`) ||
      this.getUserPermissions().some((p) => p.toLowerCase().startsWith(`${systemKey}:`));
  }

  getUserPermissions(): string[] {
    const p = this.auth.claims?.perm;
    if (Array.isArray(p)) return p;
    if (typeof p === 'string') return [p];
    return [];
  }

  private normalizeSystemKey(key?: string | null): string {
    const k = (key ?? '').trim().toLowerCase();
    return k === 'curalab' ? 'lab' : k;
  }

  getAppIcon(key: string): string {
    const icons: Record<string, string> = {
      'lab': 'CL',
      'curalab': 'CL', // Legacy support
      'hr': 'HR',
      'insurance': 'IN',
      'patient': 'PT',
      'doctor': 'DR',
      'referrallab': 'RL',
      'auth': 'AA',
    };
    return icons[key.toLowerCase()] || key.substring(0, 2).toUpperCase();
  }

  getAppDescription(key: string): string {
    const keys: Record<string, string> = {
      'lab': 'APPS.LAB_DESC',
      'curalab': 'APPS.LAB_DESC',
      'hr': 'APPS.HR_DESC',
      'insurance': 'APPS.INSURANCE_DESC',
      'patient': 'APPS.PATIENT_DESC',
      'doctor': 'APPS.DOCTOR_DESC',
      'referrallab': 'APPS.REFERRAL_LAB_DESC',
      'auth': 'APPS.AUTH_DESC',
    };
    const transKey = keys[key.toLowerCase()] || 'APPS.GENERIC_DESC';
    return this.translate.instant(transKey);
  }

  async openApp(app: SystemApp) {
    if (!this.hasAccessToApp(app)) {
      return;
    }

    const systemKey = app.key.toLowerCase();
    const appUrl = environment.systemUrls[systemKey];

    if (!appUrl) {
      console.warn(`No URL configured for system: ${systemKey}`);
      alert(`لم يتم تكوين رابط للتطبيق: ${app.displayName}`);
      return;
    }

    // NEW APPROACH: Use loginByToken directly instead of authorization code flow
    // Get access token from AuthService
    const accessToken = this.accessToken;
    if (!accessToken) {
      alert('يجب تسجيل الدخول أولاً');
      return;
    }

    // إزالة # و ? أولاً ثم الشرطات الزائدة — وإلا يصبح http://localhost:4200/#/ → ...4200/ ثم + "/" = //
    const base = (appUrl.split('#')[0] ?? appUrl).split('?')[0] ?? appUrl;
    const normalizedAppUrl = base.replace(/\/+$/, '');

    // Pass the access token through the URL **fragment** (after `#`). The fragment
    // is never sent to any server, so it stays out of access logs and proxy
    // history. The launched app's AuthTokenBootstrapService reads it and
    // immediately strips it via history.replaceState. This is required because
    // AuthPortal stores the token in sessionStorage, which is tab-scoped — a
    // freshly opened tab cannot see it, leading to a login redirect loop.
    const tokenParam = 'authToken=' + encodeURIComponent(accessToken);
    const openUrl = `${normalizedAppUrl}/#/?${tokenParam}`;

    window.open(openUrl, '_blank');
  }

  private getAuthBaseUrl(): string {
    // Use API base URL from environment
    return environment.apiBase;
  }

  private generateCodeVerifier(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    const base64 = btoa(String.fromCharCode(...Array.from(array)));
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  }

  private async sha256Base64Url(str: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(str);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const base64 = btoa(String.fromCharCode(...hashArray));
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  }

  private generateState(): string {
    return Math.random().toString(36).substring(2, 15) + 
           Math.random().toString(36).substring(2, 15);
  }
}

